/* Hero carousel. Swiper 11, loaded globally. Rotates on desktop only.

   The fitter sizes every headline line to the same measure, so each line fills
   the width it has and the block is flush down both edges. It writes a pixel
   size to --hero-fit-size and the stylesheet's own clamp is the fallback
   underneath it. It fits immediately and again once Termina has loaded, because
   measured in the fallback face the size comes out wrong.

   Because the size is set per line there is no oversized line setting any more.
   Hero Headline Large Line was removed from the promotion metaobject on
   2026-09-08. The red line, which is colour, is unaffected.

   The measure itself is capped in homepage-2026.css at 618px, which is the copy
   panel's own width at a 650px viewport. Below that the heading runs full width;
   above it the cap holds, because the panel keeps growing to 977px at 1024 and a
   line filling that renders at about 148px.

   On desktop the image keeps its own ratio and sets the slide height, so the
   copy has to fit that height. Where it does not, the whole panel is scaled
   down through --hero-copy-scale, and the heading measure narrows with it
   through --hero-measure so the headline stays a flush block. Where even the
   floor will not fit, the copy stays at the floor and hero-carousel--unlocked
   lets the band stretch to it, so the image crops only by what is left over.

   Swiper's loop clones the slides at init, so the fit runs again afterwards to
   catch them.

   The tag in sections/hero-carousel.liquid is deliberately not deferred: a
   deferred 1.5KB script queued behind 389 other requests on this page and did
   not execute until 3.5s, which is the whole of the delay before the headline
   snapped into place. */

(function () {
  'use strict';

  const DESKTOP = '(min-width: 1025px)';

  /* Bounds in pixels, because the fitter sets the size rather than scaling
     something else. A one word headline would otherwise fill the measure at any
     size at all: SALE across the 618px measure wants 188px. The ceiling has to
     clear what real copy asks for or a short line stops filling while its
     neighbours do, which is the one thing that breaks the flush block: RELY ON
     needs 112px at that measure. */
  const FIT_MIN = 16;
  const FIT_MAX = 120;

  /* The size below which the copy scaling stops narrowing the measure. Lower
     than this and the headline stops being the point of the slide. FIT_MIN
     sits under it so a long line shrinks to fit rather than wrapping. */
  const MEASURE_MIN = 24;

  /* Glyph advances do not scale perfectly linearly with font size, so a ratio
     taken at one size lands a fraction over the measure at another. Measured
     at 100.1% before this was added, which is enough to wrap a line. */
  const SAFETY = 0.995;

  const range = document.createRange();

  /* A Range measures the text. The span itself is display: block, so its own
     box is the whole measure at every size and reports one client rect however
     the text inside it behaves. */
  const textWidth = (line) => {
    range.selectNodeContents(line);
    return range.getBoundingClientRect().width;
  };

  const lineWraps = (line) => {
    range.selectNodeContents(line);
    return range.getClientRects().length > 1;
  };

  /* Every line is fitted to the same measure, so the block is flush down both
     edges and each line carries its own size. Clearing the property first puts
     the line back on the stylesheet's own size, which is what the ratio below
     is measured against. */
  const fitLine = (line, available) => {
    line.style.removeProperty('--hero-fit-size');

    /* nowrap while measuring. A line that has wrapped reports the width of its
       widest fragment, and feeding that back in makes the next size larger,
       which wraps it harder. Held for the whole calculation, not just the
       first read. */
    line.style.whiteSpace = 'nowrap';

    const natural = textWidth(line);
    const current = parseFloat(window.getComputedStyle(line).fontSize);

    if (!available || !natural || !current) {
      line.style.whiteSpace = '';
      return;
    }

    const bound = (px) => Math.min(FIT_MAX, Math.max(FIT_MIN, px));

    let size = bound((available / natural) * current * SAFETY);
    line.style.setProperty('--hero-fit-size', size.toFixed(2) + 'px');

    /* One correction pass. Glyph advances are not perfectly linear in the size,
       so the first estimate lands between about 98% and 100% of the measure.
       Re-measuring at the size actually applied takes every line to within half
       a percent, which is what makes the block read as flush. */
    const applied = textWidth(line);
    if (applied) {
      size = bound(size * (available / applied) * SAFETY);
      line.style.setProperty('--hero-fit-size', size.toFixed(2) + 'px');
    }

    line.style.whiteSpace = '';

    /* Only reachable when the bounds above stopped the line reaching a size
       that fits, which means the copy is too long for the measure. Shrinking
       past FIT_MIN is worse than letting it wrap, so the loop stops there. */
    let guard = 0;
    while (lineWraps(line) && size > FIT_MIN && guard < 20) {
      size = Math.max(FIT_MIN, size * 0.98);
      line.style.setProperty('--hero-fit-size', size.toFixed(2) + 'px');
      guard += 1;
    }
  };

  const fitHeading = (heading) => {
    const available = heading.clientWidth;
    heading.querySelectorAll('.hero-carousel__heading-line').forEach((line) => {
      fitLine(line, available);
    });
  };

  /* The smallest the copy panel is scaled to before the image ratio is given
     up. Below this the body copy reaches its 13px floor and the headline gets
     too small to be the point of the slide. */
  const SCALE_MIN = 0.55;

  const pixels = (value) => parseFloat(value) || 0;

  /* The copy fits when what it needs is no taller than the image beside it.
     Measured off the content, because the panel stretches to the row and its
     own height says nothing about what is inside it. */
  const copyFits = (copy) => {
    const band = copy.parentElement.querySelector('.hero-carousel__band');
    if (!band) return true;
    range.selectNodeContents(copy);
    const content = range.getBoundingClientRect().height;
    const style = window.getComputedStyle(copy);
    const needed = content + pixels(style.paddingTop) + pixels(style.paddingBottom);
    return needed <= band.getBoundingClientRect().height + 0.5;
  };

  const resetCopy = (copy) => {
    copy.style.removeProperty('--hero-copy-scale');
    const heading = copy.querySelector('.hero-carousel__heading');
    if (heading) heading.style.removeProperty('--hero-measure');
    return heading;
  };

  /* Binary search on the scale, eight passes, which lands within half a
     percent of the largest size that fits. */
  const fitCopy = (copy) => {
    const heading = resetCopy(copy);
    if (heading) fitHeading(heading);
    if (copyFits(copy)) return true;

    const full = heading ? heading.clientWidth : 0;

    /* The measure never narrows past the point where the longest line would
       drop below MEASURE_MIN. Past that the line wraps and the copy gets taller as
       the scale falls, which is what made the first version give up on copy
       that fitted comfortably at 0.93. */
    let floor = 0;
    if (heading) {
      heading.querySelectorAll('.hero-carousel__heading-line').forEach((line) => {
        const size = parseFloat(window.getComputedStyle(line).fontSize);
        if (size) floor = Math.max(floor, (full * MEASURE_MIN) / size / SAFETY);
      });
    }

    const apply = (scale) => {
      copy.style.setProperty('--hero-copy-scale', scale.toFixed(3));
      if (heading) {
        const measure = Math.min(full, Math.max(full * scale, floor));
        heading.style.setProperty('--hero-measure', measure.toFixed(1) + 'px');
        fitHeading(heading);
      }
    };

    let low = SCALE_MIN;
    let high = 1;
    apply(low);

    /* Left at the floor rather than reset, so the band only stretches by what
       is still over and the crop grows from nothing instead of jumping. */
    if (!copyFits(copy)) return false;

    for (let pass = 0; pass < 8; pass += 1) {
      const mid = (low + high) / 2;
      apply(mid);
      if (copyFits(copy)) {
        low = mid;
      } else {
        high = mid;
      }
    }

    apply(low);
    return true;
  };

  const fitAll = (root) => {
    const copies = root.querySelectorAll('.hero-carousel__copy');
    root.classList.remove('hero-carousel--unlocked');

    if (!window.matchMedia(DESKTOP).matches) {
      copies.forEach(resetCopy);
      root.querySelectorAll('.hero-carousel__heading').forEach(fitHeading);
      return;
    }

    let locked = true;
    copies.forEach((copy) => {
      if (!fitCopy(copy)) locked = false;
    });

    if (!locked) root.classList.add('hero-carousel--unlocked');
  };

  /* The face this heading actually asks for, read off the element rather than
     named here, so changing the theme's font does not silently break the wait. */
  const headingFontSpec = (root) => {
    const line = root.querySelector('.hero-carousel__heading-line');
    if (!line) return '';
    const style = window.getComputedStyle(line);
    return style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily;
  };

  /* document.fonts.ready waits for every font on the page. On this store that
     includes a second Typekit kit an app loads, which finished 7.7s into the
     load when this was measured, so the headline sat at the wrong size until
     then. Waiting on this heading's own face instead resolves as soon as
     Termina is there. */
  const whenFontReady = (root) => {
    if (!document.fonts || !document.fonts.load) return null;

    const spec = headingFontSpec(root);
    if (!spec) return null;

    /* No document.fonts.check() shortcut here. check() answers whether the text
       can be rendered with the stack it was given, and the stack ends in
       sans-serif, so it returns true whether or not Termina has arrived. It
       returns true for a family that does not exist. load() is always called
       instead: it resolves immediately when the face is already there, and the
       second fit costs a handful of measurements. */
    try {
      return document.fonts.load(spec).catch(() => {});
    } catch (error) {
      return null;
    }
  };

  const watchFit = (root) => {
    if (root.dataset.heroFitted === 'true') return;
    root.dataset.heroFitted = 'true';

    fitAll(root);

    const pending = whenFontReady(root);
    if (pending) pending.then(() => fitAll(root));

    if (typeof window.ResizeObserver === 'undefined') return;

    let frame = 0;
    const observer = new window.ResizeObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => fitAll(root));
    });

    /* The bands as well as the section. Swiper sets slide widths after the
       window has resized, so a fit triggered by the section alone can measure
       the previous width, which left the hero unlocked after widening. */
    const observeAll = () => {
      observer.observe(root);
      root.querySelectorAll('.hero-carousel__band').forEach((band) => observer.observe(band));
    };

    observeAll();
    root.heroObserveAll = observeAll;
  };

  const init = (root) => {
    /* Fit first and independently. The headline has no reason to wait for a
       carousel library, and if Swiper ever fails to load the copy should still
       be the right size. */
    watchFit(root);

    if (root.dataset.heroInitialised === 'true') return;

    const container = root.querySelector('.hero-carousel__swiper');
    if (!container || typeof window.Swiper === 'undefined') return;

    const slideCount = parseInt(root.dataset.slideCount, 10) || 0;
    const wantsAutoplay = root.dataset.autoplay === 'true' && slideCount > 1;
    const delay = (parseInt(root.dataset.autoplayDelay, 10) || 6) * 1000;
    const isDesktop = window.matchMedia(DESKTOP).matches;

    const swiper = new window.Swiper(container, {
      slidesPerView: 1,
      loop: slideCount > 1,
      speed: 500,
      autoHeight: false,
      watchOverflow: true,
      autoplay:
        wantsAutoplay && isDesktop
          ? { delay: delay, pauseOnMouseEnter: true, disableOnInteraction: true }
          : false,
      navigation: {
        prevEl: root.querySelector('.hero-carousel__nav--prev'),
        nextEl: root.querySelector('.hero-carousel__nav--next')
      },
      pagination: {
        el: root.querySelector('.hero-carousel__pagination'),
        clickable: true
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide'
      }
    });

    if (wantsAutoplay) {
      const query = window.matchMedia(DESKTOP);
      const onChange = (event) => {
        if (!swiper.autoplay) return;
        if (event.matches) {
          swiper.autoplay.start();
        } else {
          swiper.autoplay.stop();
        }
      };

      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', onChange);
      } else if (typeof query.addListener === 'function') {
        query.addListener(onChange);
      }
    }

    /* Swiper's loop clones the slides, so the clones need fitting and
       observing too. */
    fitAll(root);
    if (root.heroObserveAll) root.heroObserveAll();

    root.dataset.heroInitialised = 'true';
  };

  const initAll = () => {
    document.querySelectorAll('.hero-carousel').forEach(init);
  };

  /* No DOMContentLoaded wrapper. The tag is not deferred and sits below the
     markup it reads, so waiting for the event only postpones the fit to the end
     of the document. It cost 1.5 seconds of wrongly sized headline when it was
     there. */
  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('.hero-carousel');
    if (root) init(root);
  });
})();
